import type { HTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

import { cn } from '@/shared/utils/cn';

/**
 * The surface every block of the app sits on: a sheet of paper on the desk.
 *
 * `title` and `description` make it a labelled `<section>`; without them it is a plain
 * box. `aside` goes to the right of the title (a badge, a button, a count). The header is
 * low on purpose (15 px title, `py-3`): the title names the block, the content is the
 * block.
 *
 * `padded={false}` removes the body padding, for a table that bleeds to the edges. Use
 * it instead of `bodyClassName="p-0"`: `cn` does not merge Tailwind classes and `.p-0` is
 * generated **before** `.p-4` in the stylesheet, so the override never won. A `p-0` in
 * `bodyClassName` is honoured anyway, so the screens that already asked for it get what
 * they meant.
 */
export function Card({
  title,
  description,
  aside,
  padded,
  className,
  bodyClassName,
  children,
  as: Tag = 'section',
  ...rest
}: {
  title?: ReactNode;
  description?: ReactNode;
  aside?: ReactNode;
  /** Body padding. Default `true`; `false` for a table or a list that bleeds to the edges. */
  padded?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
  as?: 'section' | 'div' | 'article';
} & Omit<HTMLAttributes<HTMLElement>, 'title'>) {
  const headingId = useId();
  const labelled = title ? { 'aria-labelledby': headingId } : {};
  const withPadding = padded ?? !/(^|\s)p-0(\s|$)/.test(bodyClassName ?? '');
  return (
    <Tag
      {...labelled}
      className={cn(
        'bg-card border-line rounded-card shadow-card border',
        'flex flex-col',
        className,
      )}
      {...rest}
    >
      {title ? (
        <header className="border-line-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b px-4 py-3">
          <div className="min-w-0">
            <h2 id={headingId} className="text-ink text-[15px] leading-5 font-semibold">
              {title}
            </h2>
            {description ? (
              <p className="text-ink-3 mt-0.5 text-[13px] leading-snug">{description}</p>
            ) : null}
          </div>
          {aside ? <div className="flex shrink-0 items-center gap-2">{aside}</div> : null}
        </header>
      ) : null}
      <div className={cn('flex flex-col gap-3', withPadding && 'p-4', bodyClassName)}>
        {children}
      </div>
    </Tag>
  );
}
